// HTTP client for the console PASSWORD-session endpoints (ADR-118/ADR-119
// Phase 7 slice A: services/console/server/session.go). These are PLAIN HTTP
// routes on the same 8130 mux as the gRPC-Web front door (see session.go's
// package doc for why Set-Cookie / CSRF are HTTP concerns, not gRPC ones), so
// they are called with fetch, NOT through the gRPC-Web client.
//
// Every call uses credentials: 'include' so the browser SETS the Set-Cookie
// response (login) and SENDS the session cookie back (logout / change-password)
// — same-origin (Phase 5C single-host Ingress) means this needs no CORS config.
// Paths are relative (same origin as the SPA), matching client.ts's same-origin
// design; VITE_CONSOLE_GRPC_WEB_URL is a gRPC-only dev escape hatch and does
// NOT apply to these routes (they live on the same origin as the served UI).

// LoginSuccess mirrors session.go's loginResponse JSON body. csrf_token is the
// double-submit token the SPA echoes as X-CSRF-Token on mutating gRPC calls;
// it is ALSO set as the non-HttpOnly console_csrf cookie, so the SPA can hold
// it in memory (preferred — see session.ts) without reading document.cookie.
export interface LoginSuccess {
  csrfToken: string
  operatorId: string
  email: string
  role: string
}

// LoginError distinguishes the generic invalid-credentials case (which the
// server returns identically for unknown-email / wrong-password / DISABLED
// operator — no enumeration oracle) from other failures. The UI shows one
// generic message regardless; `status` is carried for callers that want to
// branch (none do today) and for clearer test assertions.
export class LoginError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.name = 'LoginError'
    this.status = status
  }
}

interface JsonErrorBody {
  error?: string
}

async function parseError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as JsonErrorBody
    if (body && typeof body.error === 'string' && body.error) return body.error
  } catch {
    // non-JSON body; fall through to a status-derived message
  }
  return `request failed (${res.status})`
}

// login POSTs {email,password} to /auth/login. On 200 it returns the parsed
// session identity + CSRF token; the browser has, by then, stored the HttpOnly
// session cookie and the console_csrf cookie (Set-Cookie on the same response).
// On any non-200 it throws a LoginError carrying the server's message. The
// caller (auth-context) treats a 401 as the generic "invalid email or password"
// and shows the server's message for other statuses (e.g. a disabled-operator
// case — though the server deliberately returns the same 401 for that too).
export async function login(email: string, password: string): Promise<LoginSuccess> {
  const res = await fetch('/auth/login', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  })
  if (!res.ok) {
    throw new LoginError(await parseError(res), res.status)
  }
  const body = (await res.json()) as {
    csrf_token: string
    operator_id: string
    email: string
    role: string
  }
  return {
    csrfToken: body.csrf_token,
    operatorId: body.operator_id,
    email: body.email,
    role: body.role,
  }
}

// logout POSTs to /auth/logout to revoke the server-side session row and clear
// the cookies. Idempotent and best-effort: the server always returns 200 (even
// with no/invalid cookie), and even a network failure here should not trap the
// operator in the app — the caller clears local state regardless. CSRF is NOT
// required by the server for logout (a forged logout only ends the victim's own
// session — self-limiting), so no X-CSRF-Token is sent.
export async function logout(): Promise<void> {
  try {
    await fetch('/auth/logout', { method: 'POST', credentials: 'include' })
  } catch {
    // Swallow — local teardown proceeds regardless. Worst case the server-side
    // row lingers until its 12h TTL; the cookie is cleared client-side anyway.
  }
}

// changePassword POSTs {old_password,new_password} to /auth/change-password.
// The endpoint is SESSION-authenticated (cookie) AND CSRF-gated, so the caller
// must pass the current session's CSRF token; it is sent as X-CSRF-Token. On
// success the server revokes ALL of the operator's sessions (including this
// one) and clears the cookies — the SPA must therefore treat a successful
// change as a logout and send the operator back to the login screen.
export async function changePassword(
  oldPassword: string,
  newPassword: string,
  csrfToken: string,
): Promise<void> {
  const res = await fetch('/auth/change-password', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body: JSON.stringify({ old_password: oldPassword, new_password: newPassword }),
  })
  if (!res.ok) {
    // The server returns specific, non-enumerating messages here (wrong current
    // password, weak new password, etc.) — surface them so the operator can
    // correct the input. Not a LoginError (different flow); a plain Error with
    // the server's message.
    throw new Error(await parseError(res))
  }
}
